import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Alert, Box, Button, IconButton, Tooltip } from '@mui/material';
import ButtonCustom from 'components/ButtonCustom';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getRevisionByIdWithProject } from 'services/revisions';
import { USER_ROLES } from 'utils/constants';
import { IRevisionWithProjectResponse } from 'utils/contracts';
import ProjectBanner from '../components/ProjectBanner';
import UseAuthContext from '../context/AuthContext';

function formatDateDDMMYYYY(value?: string | null) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

const IframeCvViewer: React.FC = () => {
  const { isAuthenticated, auth, organizationId, role, organizations } = useContext(UseAuthContext);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const revisionId = searchParams.get('revisionId');
  const projectId = searchParams.get('projectId');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [mosquitoModalOpen, setMosquitoModalOpen] = useState(false);
  const [isPdfExportReady, setIsPdfExportReady] = useState(false);
  const { data: revisionWithProjectData, isLoading } = useQuery<IRevisionWithProjectResponse>(
    ['getRevisionByIdWithProject', revisionId, projectId],
    () => getRevisionByIdWithProject(revisionId, projectId, organizationId),
    {
      enabled:
        !!revisionId &&
        !!projectId &&
        (!!organizationId ||
          role === USER_ROLES.INSPECTOR ||
          role === USER_ROLES.INSPECTOR_MANAGER ||
          role === USER_ROLES.ADMIN)
    }
  );

  const projectHeader = useMemo(() => {
    const r = revisionWithProjectData;
    if (!r) return null;
    return {
      organizationName: r.organization_name || organizations?.company_name || '',
      projectName: r.project_name || '',
      projectType: r.project_type || '',
      revisionId: r.revision_id || revisionId || '',
      climate: r.project_climate || '',
      terrain: r.project_terrain || '',
      rating: r.revision_rating || '',
      version: r.project_version || '',
      dateText: formatDateDDMMYYYY(r.revision_updatedAt),
    };
  }, [revisionWithProjectData, organizations?.company_name, revisionId]);

  const postToIframe = useCallback((message: Record<string, unknown>) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(message, '*');
    }
  }, []);

  const pushProjectHeader = useCallback(() => {
    if (!projectHeader) return;
    postToIframe({ type: 'SET_PROJECT_HEADER', projectHeader });
  }, [postToIframe, projectHeader]);

  const closeMosquitoModal = useCallback(() => {
    setMosquitoModalOpen(false);
    postToIframe({ type: 'CV_MOSQUITO_ACK' });
  }, [postToIframe]);

  const handleExportPdf = useCallback(() => {
    postToIframe({ type: 'CV_EXPORT_PDF' });
  }, [postToIframe]);

  useEffect(() => {
    setIsPdfExportReady(false);
  }, [code]);

  const handleIframeLoad = () => {
    if (!iframeRef.current) return;
    setMosquitoModalOpen(false);
    if (auth?.token) {
      postToIframe({
        type: 'SET_TOKEN',
        token: auth.token,
        organizationId: organizationId || null,
        projectHeader: projectHeader || undefined,
      });
      setTimeout(() => {
        postToIframe({
          type: 'SET_TOKEN',
          token: auth.token,
          organizationId: organizationId || null,
          projectHeader: projectHeader || undefined,
        });
        pushProjectHeader();
      }, 500);
    } else {
      pushProjectHeader();
    }
  };

  useEffect(() => {
    pushProjectHeader();
  }, [pushProjectHeader]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const allowedOrigins = [
        window.location.origin,
        process.env.REACT_APP_HOST || 'http://localhost:3000'
      ];

      if (!allowedOrigins.includes(event.origin)) {
        return;
      }

      if (event.data?.type === 'TOKEN_SET') {
        console.log('Token successfully set in CV viewer');
      }
      if (event.data?.type === 'REQUEST_TOKEN' && auth?.token) {
        postToIframe({
          type: 'SET_TOKEN',
          token: auth.token,
          organizationId: organizationId || null,
          projectHeader: projectHeader || undefined,
        });
      }
      if (event.data?.type === 'CV_REPORT_READY') {
        setIsPdfExportReady(true);
        pushProjectHeader();
      }
      if (event.data?.type === 'CV_MOSQUITO_MODAL_OPEN') {
        setMosquitoModalOpen(true);
      }
      if (event.data?.type === 'CV_MOSQUITO_ACK') {
        setMosquitoModalOpen(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [pushProjectHeader, auth?.token, organizationId, postToIframe, projectHeader]);

  useEffect(() => {
    if (!isAuthenticated) {
      const currentPath = location.pathname + location.search;
      navigate('/login', { state: { redirect: currentPath } });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    if (
      revisionWithProjectData &&
      organizationId &&
      role !== USER_ROLES.ADMIN &&
      role !== USER_ROLES.INSPECTOR &&
      role !== USER_ROLES.INSPECTOR_MANAGER
    ) {
      const revisionOrganizationId = revisionWithProjectData.organization_id;
      if (organizationId !== revisionOrganizationId) {
        console.error('Access denied: User does not belong to the same organization as the revision');
      }
    }
  }, [revisionWithProjectData, organizationId, role]);

  if (!code) {
    return (
      <Box sx={{ p: 2, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error">
          No revision code provided. Please add ?code=YOUR_CODE to the URL.
        </Alert>
      </Box>
    );
  }

  if (
    revisionWithProjectData &&
    organizationId &&
    organizationId !== revisionWithProjectData.organization_id &&
    role !== USER_ROLES.ADMIN &&
    role !== USER_ROLES.INSPECTOR &&
    role !== USER_ROLES.INSPECTOR_MANAGER
  ) {
    return (
      <Box
        sx={{
          p: 2,
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}
      >
        <Alert severity="error">{t('3D_VIEWER_ACCESS_DENIED')}</Alert>
        <ButtonCustom variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
          {t('3D_VIEWER_BUTTON_BACK_TO_PROJECTS')}
        </ButtonCustom>
      </Box>
    );
  }

  const backendHost = process.env.REACT_APP_HOST || 'http://localhost:3000';
  const orgQuery = organizationId ? `&organizationId=${encodeURIComponent(organizationId)}` : '';
  const viewerUrl = `${backendHost}/idf/cv?code=${code}${orgQuery}`;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ProjectBanner
        isLoading={isLoading}
        revisionWithProjectData={revisionWithProjectData}
        headerActions={
          <Tooltip title={t('REVISION_V4_EXPORT_PDF')} placement="bottom">
            <span>
              <ButtonCustom
                variant="contained"
                disabled={!isPdfExportReady}
                onClick={handleExportPdf}
                sx={{
                  minWidth: '56px',
                  width: '56px',
                  height: '56px',
                  borderRadius: '8px',
                  px: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textTransform: 'none',
                  boxShadow: 'none',
                  bgcolor: '#0E66F0',
                  '&:hover': {
                    bgcolor: '#0052D3',
                    boxShadow: 'none',
                  }
                }}
                aria-label={t('REVISION_V4_EXPORT_PDF')}
              >
                <PictureAsPdfIcon />
              </ButtonCustom>
            </span>
          </Tooltip>
        }
      />

      <Box sx={{ flexGrow: 1, marginTop: '80px', overflow: 'auto' }}>
        <iframe
          ref={iframeRef}
          src={viewerUrl}
          width="100%"
          height="100%"
          title={`${t('CV_VIEWER_BUTTON')} Viewer`}
          style={{
            border: 'none',
            borderRadius: '0',
            boxShadow: 'none',
            minHeight: 'calc(100vh - 80px)'
          }}
          onLoad={handleIframeLoad}
        />
      </Box>

      {mosquitoModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cv-mosquito-modal-title"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            boxSizing: 'border-box',
            background: 'rgba(15, 23, 42, 0.55)',
          }}
        >
          <div
            style={{
              position: 'relative',
              width: 'min(440px, 100%)',
              minHeight: 200,
              background: '#fff',
              borderRadius: 10,
              padding: '32px 24px 28px',
              boxShadow: '0 18px 40px rgba(15, 23, 42, 0.22)',
              textAlign: 'right',
              direction: 'rtl',
            }}
          >
            <IconButton
              aria-label={t('CLOSE_BUTTON')}
              onClick={closeMosquitoModal}
              size="small"
              style={{
                position: 'absolute',
                top: 8,
                left: 8,
                color: '#546e7a',
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <h3
              id="cv-mosquito-modal-title"
              style={{
                margin: '0 0 14px',
                paddingLeft: 36,
                paddingRight: 0,
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#1a1a2e',
                lineHeight: 1.35,
                textAlign: 'right',
                direction: 'rtl',
              }}
            >
              תנאי רשתות נגד יתושים
            </h3>
            <p
              style={{
                margin: '0 0 28px',
                fontSize: '0.95rem',
                color: '#37474f',
                lineHeight: 1.5,
                textAlign: 'right',
                direction: 'rtl',
              }}
            >
              תנאי רשתות נגד יתושים — 10 הקומות הראשונות
            </p>
            <div style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                onClick={closeMosquitoModal}
                style={{
                  fontWeight: 600,
                  borderRadius: 8,
                  padding: '10px 18px',
                  textTransform: 'none',
                  direction: 'rtl',
                }}
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                הבנתי, בדוק אוורור מפולש
              </Button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default IframeCvViewer;
